/**
 * NotificationsBell — navbar bell with unread badge and an anchored dropdown
 * (not a Modal). Data lives under the ['notifications'] query key; the WS
 * layer (lib/ws.ts) invalidates it on push, so the badge is live.
 * RTL-safe: logical properties only (`end-0` anchors the panel correctly).
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { api } from '../lib/api';
import { Check, Info, TriangleAlert } from './icons';
import { Button, IconButton } from './ui';

interface NotificationRow {
  id: string;
  level: string;
  messageKey: string;
  params: Record<string, unknown> | null;
  read: boolean;
  createdAt: string;
}

/** Level → icon + tone. Server sends 'success'/'error'; be liberal in what we accept. */
function LevelIcon({ level }: { level: string }) {
  if (level === 'success' || level === 'positive') {
    return (
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-positive/15 text-positive" aria-hidden>
        <Check className="size-3" />
      </span>
    );
  }
  if (level === 'error' || level === 'negative') {
    return (
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-negative/15 text-negative" aria-hidden>
        <TriangleAlert className="size-3" />
      </span>
    );
  }
  return (
    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-surface-3 text-muted" aria-hidden>
      <Info className="size-3" />
    </span>
  );
}

/** Relative time ("5 minutes ago") in the active locale — no dependency needed. */
function timeAgo(iso: string, locale: string): string {
  const diffSec = (new Date(iso).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(diffSec) >= secs) return rtf.format(Math.round(diffSec / secs), unit);
  }
  return rtf.format(Math.round(diffSec), 'second');
}

export function NotificationsBell() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications(),
    staleTime: 30_000, // WS invalidates on push; this is just a floor
  });
  const notifications: NotificationRow[] = data?.notifications ?? [];
  const unread = notifications.filter((n) => !n.read).length;

  const markAll = useMutation({
    mutationFn: () => api.notificationsRead(),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Close on outside click + Escape while open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Server may send `notifications.jobSucceeded`; the UI namespace is `notif.*`.
  // Try the key as-is, then the mapped namespace, then show the raw key.
  const label = (key: string): string => {
    const direct = t(key);
    if (direct !== key) return direct;
    const mapped = key.replace('notifications.', 'notif.');
    const alt = t(mapped);
    return alt !== mapped ? alt : key;
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <IconButton
          label={t('notif.title')}
          active={open}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <Bell className="size-4" />
        </IconButton>
        {unread > 0 && (
          <span
            className="pointer-events-none absolute -top-0.5 -end-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold tabular text-accent-contrast"
            aria-hidden
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </div>

      {open && (
        <div
          className="absolute end-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
          style={{ animation: 'slide-down 0.16s ease-out both' }}
          role="region"
          aria-label={t('notif.title')}
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
            <p className="text-sm font-semibold">{t('notif.title')}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAll.mutate()}
              loading={markAll.isPending}
              disabled={unread === 0}
            >
              {t('notif.markAll')}
            </Button>
          </div>

          {notifications.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted">{t('notif.empty')}</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {notifications.map((n) => (
                <li key={n.id} className="flex items-start gap-2.5 px-4 py-2.5">
                  <LevelIcon level={n.level} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm ${n.read ? 'text-muted' : 'font-medium text-text'}`}>{label(n.messageKey)}</p>
                    <p className="mt-0.5 text-xs text-muted">{timeAgo(n.createdAt, i18n.language)}</p>
                  </div>
                  {!n.read && <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" aria-hidden />}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
